import { EQpQuestionType } from '@library/models/qp-question.models';
import { QpLoggerService } from '@library/services/qp-logger/qp-logger.service';
import { SERVER_API_URL } from '@one/app/app.constants';
import { ActionsQueueSyncService } from '@one/app/components/main/services/actions-queue-sync.service';
import { saveTestAnswerSuccess } from '@one/app/pages/isp/pages/inspection/pages/id/shared/services/store/isp-inspection-id-workflow-store.actions';
import { InspectionId } from '@one/app/pages/isp/shared/models/isp-inspection.models';
import { Answer } from '@one/app/shared/models/answer/answer.models';
import { ISaveTestChecklistAnswerData } from '@one/app/shared/services/database/database-inspection.models';
import { DatabaseService } from '@one/app/shared/services/database/database-inspection.service';
import { IStoreState } from '@one/app/store/store.models';
import { HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

const TEXT_DEXIE_DEBOUNCE_MS = 100;

type TextAnswerPendingKey = string;

type PendingControlFieldAnswerCommit = {
  inspectionId: InspectionId;
  answer: Answer | null;
  answerId: string;
  type: EQpQuestionType;
  entryUuid?: string;
};

@Injectable({
  providedIn: 'root',
})
export class TestChecklistAnswerSyncService {
  private readonly _databaseService = inject(DatabaseService);
  private readonly _actionsQueueSyncService = inject(ActionsQueueSyncService);
  private readonly _store = inject(Store<IStoreState>);
  private readonly _qpLoggerService = inject(QpLoggerService);

  private readonly _textDexieTimers = new Map<TextAnswerPendingKey, ReturnType<typeof setTimeout>>();
  private readonly _textPendingCommits = new Map<TextAnswerPendingKey, PendingControlFieldAnswerCommit>();
  private readonly _textFlushChains = new Map<TextAnswerPendingKey, Promise<void>>();

  /**
   * Updates NgRx immediately and persists the answer to Dexie without HTTP.
   */
  public async commitTestAnswerLocally(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): Promise<void> {
    this._commitTestAnswerToStore(answerId, value, elementType, entryUuid);
    await this._commitTestAnswerToDexie(inspectionId, answerId, value, elementType, entryUuid);
  }

  /**
   * Enqueues (or coalesces) a `PATCH …/workflow` outbox action for the answer.
   */
  public async scheduleAnswerSync(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): Promise<void> {
    const body: ISaveTestChecklistAnswerData = {
      answer: value,
      answerId,
      type: elementType,
      ...(entryUuid ? { entryUuid } : {}),
    };
    const url = `${SERVER_API_URL}api/inspections/${inspectionId}/workflow`;
    const request = new HttpRequest<ISaveTestChecklistAnswerData>('PATCH', url, body);

    await this._actionsQueueSyncService.addAction(request);
  }

  /**
   * Local-first path for non-TEXT types: store + Dexie + outbox in one call.
   */
  public async commitAndScheduleAnswerSync(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): Promise<void> {
    await this.commitTestAnswerLocally(inspectionId, answerId, value, elementType, entryUuid);
    await this.scheduleAnswerSync(inspectionId, answerId, value, elementType, entryUuid);
  }

  /**
   * Debounced local-first path for single-value control fields (TEXT, BARCODE_SCAN, etc.).
   * NgRx updates immediately; Dexie + outbox are debounced at 100ms with flush support.
   */
  public commitTextAnswerToStore(answerId: string, value: Answer | null, elementType: EQpQuestionType, entryUuid?: string): void {
    this._commitTestAnswerToStore(answerId, value, elementType, entryUuid);
  }

  public scheduleTextAnswerDexieAndSync(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): void {
    const key = this._buildTextPendingKey(inspectionId, answerId, entryUuid);

    this._textPendingCommits.set(key, {
      inspectionId,
      answer: value,
      answerId,
      type: elementType,
      ...(entryUuid ? { entryUuid } : {}),
    });

    const existingTimer = this._textDexieTimers.get(key);

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout((): void => {
      void this._flushTextAnswerPending(key);
    }, TEXT_DEXIE_DEBOUNCE_MS);

    this._textDexieTimers.set(key, timer);
  }

  public flushTextAnswerDexieAndSync(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): Promise<void> {
    const key = this._buildTextPendingKey(inspectionId, answerId, entryUuid);

    this._textPendingCommits.set(key, {
      inspectionId,
      answer: value,
      answerId,
      type: elementType,
      ...(entryUuid ? { entryUuid } : {}),
    });

    const existingTimer = this._textDexieTimers.get(key);

    if (existingTimer) {
      clearTimeout(existingTimer);
      this._textDexieTimers.delete(key);
    }

    return this._flushTextAnswerPending(key);
  }

  /**
   * Flushes all pending TEXT Dexie/outbox writes (page hide, inspection navigation).
   */
  public async flushPendingTextAnswerSync(): Promise<void> {
    const pendingKeys = [...this._textPendingCommits.keys()];

    for (const key of pendingKeys) {
      const existingTimer = this._textDexieTimers.get(key);

      if (existingTimer) {
        clearTimeout(existingTimer);
        this._textDexieTimers.delete(key);
      }

      await this._flushTextAnswerPending(key);
    }
  }

  private _commitTestAnswerToStore(answerId: string, value: Answer | null, elementType: EQpQuestionType, entryUuid?: string): void {
    this._store.dispatch(
      saveTestAnswerSuccess({
        id: answerId,
        value: value as Answer,
        elementType,
        entryUuid,
      })
    );
  }

  private async _commitTestAnswerToDexie(
    inspectionId: InspectionId,
    answerId: string,
    value: Answer | null,
    elementType: EQpQuestionType,
    entryUuid?: string
  ): Promise<void> {
    const data: ISaveTestChecklistAnswerData = {
      answer: value,
      answerId,
      type: elementType,
      ...(entryUuid ? { entryUuid } : {}),
    };

    await this._databaseService.saveTestChecklistAnswer(inspectionId, data);
  }

  private _buildTextPendingKey(inspectionId: InspectionId, answerId: string, entryUuid?: string): TextAnswerPendingKey {
    return `${inspectionId}|${answerId}|${entryUuid ?? ''}`;
  }

  private _flushTextAnswerPending(key: TextAnswerPendingKey): Promise<void> {
    const snapshot = this._textPendingCommits.get(key);

    if (!snapshot) {
      return this._textFlushChains.get(key) ?? Promise.resolve();
    }

    this._textPendingCommits.delete(key);
    this._textDexieTimers.delete(key);

    const previousChain = this._textFlushChains.get(key) ?? Promise.resolve();
    const nextChain = previousChain
      .catch((error: unknown): void => {
        this._qpLoggerService.error('Previous TEXT test answer flush failed', error);
      })
      .then(async (): Promise<void> => {
        try {
          await this._commitTestAnswerToDexie(snapshot.inspectionId, snapshot.answerId, snapshot.answer, snapshot.type, snapshot.entryUuid);
          await this.scheduleAnswerSync(snapshot.inspectionId, snapshot.answerId, snapshot.answer, snapshot.type, snapshot.entryUuid);
        } catch (error: unknown) {
          this._qpLoggerService.error('Failed to persist TEXT test answer to local database or outbox', error);
        }
      });

    this._textFlushChains.set(key, nextChain);

    return nextChain.finally((): void => {
      if (this._textFlushChains.get(key) === nextChain) {
        this._textFlushChains.delete(key);
      }
    });
  }
}
